import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import WorkforceCompositionModal from '../components/WorkforceCompositionModal';
import HeadcountChangeModal from '../components/HeadcountChangeModal';
import {
  Users,
  Clock,
  MapPin,
  Calendar,
  TrendingUp,
  AlertCircle,
  AlertTriangle,
  FileText,
  ArrowUpRight,
  ArrowDownRight,
  Globe,
  ChevronRight,
  Baby,
  BarChart3,
  HeartPulse,
  Shield,
  Wallet,
  PieChart,
  Activity,
} from 'lucide-react';
import useInterimAlerts from '../hooks/useInterimAlerts';
import useTurnoverData from '../hooks/useTurnoverData';
import useComplianceData from '../hooks/useComplianceData';
import useDRCData from '../hooks/useDRCData';
import useAnalyticsData from '../hooks/useAnalyticsData';
import useDashboardMetrics from '../hooks/useDashboardMetrics';
import { useHeadcountMovements } from '../hooks/useDashboardData';

import ExecutiveSummaryStrip from '../components/dashboard/ExecutiveSummaryStrip';
import TurnoverSection from '../components/dashboard/TurnoverSection';
import WorkforcePlanningSection from '../components/dashboard/WorkforcePlanningSection';
import ComplianceExpirySection from '../components/dashboard/ComplianceExpirySection';
import LevyOptimizationSection from '../components/dashboard/LevyOptimizationSection';
import DRCSection from '../components/dashboard/DRCSection';
import MOMComplianceSection from '../components/dashboard/MOMComplianceSection';
import SiteWeatherSection from '../components/dashboard/SiteWeatherSection';
import ProjectLabourCostSection from '../components/dashboard/ProjectLabourCostSection';
import WorkforceAnalyticsSection from '../components/dashboard/WorkforceAnalyticsSection';
import UsageAnalyticsSummary from '../components/dashboard/UsageAnalyticsSummary';
import AccordionSection from '../components/UI/AccordionSection';
import {
  DOCUMENT_VERIFICATION_QUEUE_HREF,
  getDocumentVerificationTooltip,
  getPendingLeaveChip,
  getPendingLeaveTooltip,
} from '../utils/adminDashboardKpis';

const formatTenureYearsMonths = (decimalYears) => {
  if (decimalYears === null || decimalYears === undefined || isNaN(decimalYears)) return 'N/A';
  let years = Math.floor(decimalYears);
  let months = Math.round((decimalYears - years) * 12);
  if (months === 12) { years += 1; months = 0; }
  if (years === 0 && months === 0) return '0m';
  if (years === 0) return `${months}m`;
  if (months === 0) return `${years}y`;
  return `${years}y ${months}m`;
};

/**
 * Calculate attendance percentage display with proper edge case handling
 * Prefers API-provided percentage (already accounts for leave and WFH/External Activity/On Course)
 */
const calculateAttendanceDisplay = (stats) => {
  if (stats.attendancePercentage !== null && stats.attendancePercentage !== undefined && stats.attendancePercentage > 0) {
    return `${stats.attendancePercentage}%`;
  }
  
  const clockedIn = stats.clockedIn || 0;
  const totalEmployees = stats.totalEmployees || 0;
  
  if (totalEmployees === 0) {
    return 'N/A';
  }
  
  const calculated = Math.round((clockedIn / totalEmployees) * 100);
  return `${calculated}%`;
};

const getAttendanceChangeType = (stats) => {
  const percentage = stats.attendancePercentage || 0;
  if (percentage >= 90) return 'increase';
  if (percentage >= 75) return 'neutral';
  return 'decrease';
};

const getComparisonChangeType = (changePercent) => {
  const absChange = Math.abs(changePercent);
  if (absChange <= 2) return 'neutral';
  if (absChange <= 5) return 'warning';
  return changePercent < 0 ? 'decrease' : 'increase';
};

const formatAttendanceChange = (stats) => {
  if (stats.attendanceYesterdayComparison) {
    const change = stats.attendanceYesterdayComparison.change;
    const sign = change >= 0 ? '+' : '';
    return `vs Yesterday: ${sign}${change.toFixed(1)}%`;
  }
  return calculateAttendanceDisplay(stats);
};

const AdminDashboard = () => {
  // Progressive disclosure: primary groups open; secondary groups collapsed
  const [openSections, setOpenSections] = useState({
    workforceHealth: true,
    compliance: true,
    costFinance: false,
    workforceProfile: false,
    platformUsage: false,
  });
  const toggleSection = (key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // ─── Custom Hooks ──────────────────────────────────────────────────────────
  const interim = useInterimAlerts();
  const { interimOverdueData, interimOverdueLoading } = interim;

  const turnover = useTurnoverData();
  const {
    turnoverData, turnoverLoading, turnoverViewMode, setTurnoverViewMode,
    showTurnoverInfoModal, setShowTurnoverInfoModal,
    showCalculationDetails, setShowCalculationDetails,
  } = turnover;

  const compliance = useComplianceData();
  const {
    overtimeStats, overtimeAlerts, overtimeLoading,
    restDayStats, restDayLoading,
    complianceExpiryData, complianceExpiryLoading,
  } = compliance;

  const drc = useDRCData();
  const {
    drcData, drcLoading, drcViewMode, setDrcViewMode,
    drcRatio, drcRatioDisplay,
    lqsCount, migrantCount, foreignTotal, foreignShare, foreignShareDisplay,
    wpCount, spCount, epCount, localEmployeesCount,
    momLimit, maxForeignWorkers, bufferCapacity,
    ttmChartData, yoyChartData, ttmYAxisDomain, yoyYAxisDomain,
    trendRange, minDrc, maxDrc,
    yoyYears,
  } = drc;

  const analytics = useAnalyticsData();
  const {
    ageGroupData, nationalityData,
    raceData, tenureData, genderData,
    departmentData, showDepartmentModal, setShowDepartmentModal,
    averageTenure,
  } = analytics;

  const metricsHook = useDashboardMetrics({
    setAgeGroupData: analytics.setAgeGroupData,
    setClassificationAgeData: analytics.setClassificationAgeData,
    setNationalityData: analytics.setNationalityData,
    setRaceData: analytics.setRaceData,
    setTenureData: analytics.setTenureData,
    setGenderData: analytics.setGenderData,
    setDepartmentData: analytics.setDepartmentData,
    setAverageTenure: analytics.setAverageTenure,
  });
  const {
    stats, loading, analyticsStatus,
    employmentPassData, showWorkforceModal, setShowWorkforceModal,
    workforceAgeProfile, retirementRisk, newHireRetention,
    staffWorkerRatio,
  } = metricsHook;

  const [showHeadcountModal, setShowHeadcountModal] = useState(false);
  const {
    data: headcountMovements,
    isLoading: headcountMovementsLoading,
    isError: headcountMovementsError,
  } = useHeadcountMovements(showHeadcountModal);

  const headcountChangeMtd = useMemo(() => {
    if (!newHireRetention || !turnoverData) return null;
    const monthlyHires = newHireRetention?.new_hires_last_12m?.total || 0;
    const monthlySeparations = turnoverData?.rolling_12_month?.separations?.total || 0;
    if (monthlyHires === 0 && monthlySeparations === 0) return null;
    const avgMonthlyHires = Math.round(monthlyHires / 12);
    const avgMonthlySeparations = Math.round(monthlySeparations / 12);
    return { net: avgMonthlyHires - avgMonthlySeparations, joined: avgMonthlyHires, left: avgMonthlySeparations, isEstimate: true };
  }, [newHireRetention, turnoverData]);

  const statCards = [
    {
      title: 'Total Employees',
      value: stats.totalEmployees || 0,
      change: `+${stats.dataFreshness === 'real-time' ? 'Live' : '12'}`,
      changeType: 'increase',
      icon: Users,
      color: 'blue',
      link: '/admin/employees'
    },
    {
      title: 'Workforce Composition',
      value: employmentPassData?.current?.active_workforce_total || stats.totalEmployees || 0,
      change: employmentPassData?.current?.employment_classification
        ? `${employmentPassData.current.employment_classification.staff || 0} Staff · ${employmentPassData.current.employment_classification.workers || 0} Workers · ${employmentPassData.current.pmet?.available === false ? '—' : employmentPassData.current.pmet?.pmet || 0} PMET`
        : 'Loading...',
      changeType: 'neutral',
      icon: Globe,
      color: 'purple',
      onClick: () => setShowWorkforceModal(true),
      showChevron: true
    },
    {
      title: 'Today\'s Attendance',
      value: `${stats.clockedIn || 0}/${stats.totalEmployees || 0}`,
      change: formatAttendanceChange(stats),
      changeType: stats.attendanceYesterdayComparison 
        ? getComparisonChangeType(stats.attendanceYesterdayComparison.change)
        : getAttendanceChangeType(stats),
      icon: Clock,
      color: 'purple',
      link: '/admin/attendance',
      tooltip: (() => {
        const rate = stats.attendancePercentage || 0;
        const present = stats.clockedIn || 0;
        const total = stats.totalEmployees || 0;
        const comparison = stats.attendanceYesterdayComparison;
        let tooltipText = `Attendance Rate: ${rate}%\nPresent: ${present}/${total}`;
        if (comparison) {
          const changeSign = comparison.change >= 0 ? '+' : '';
          tooltipText += `\nvs Yesterday: ${changeSign}${comparison.change.toFixed(1)}%`;
        }
        tooltipText += '\n\nPercentage of employees present today (excluding those on approved leave). Includes employees on Work From Home, External Activity, and On Course as they are "at work".';
        return tooltipText;
      })()
    },
    {
      title: 'Pending Leave Requests',
      value: stats.pendingLeave || 0,
      ...getPendingLeaveChip(stats.pendingLeave),
      icon: Calendar,
      color: 'yellow',
      link: '/admin/leave?tab=applications&status=submitted',
      tooltip: getPendingLeaveTooltip(stats.pendingLeave, stats.leaveApprovalRate),
    },
    {
      title: 'Avg Monthly Change',
      value: headcountChangeMtd ? `${headcountChangeMtd.net >= 0 ? '+' : ''}${headcountChangeMtd.net}/mo` : 'N/A',
      change: headcountChangeMtd ? `${headcountChangeMtd.joined} joined · ${headcountChangeMtd.left} left (12m avg)` : 'Calculating...',
      changeType: headcountChangeMtd ? (headcountChangeMtd.net > 0 ? 'increase' : headcountChangeMtd.net < 0 ? 'decrease' : 'neutral') : 'neutral',
      icon: TrendingUp,
      color: 'emerald',
      onClick: () => setShowHeadcountModal(true),
      showChevron: true,
      tooltip: 'Average monthly joins minus separations over the trailing 12 months. Click to see who joined and left.'
    },
    {
      title: 'Average Tenure',
      value: averageTenure != null ? formatTenureYearsMonths(averageTenure) : 'N/A',
      change: 'Workforce stability',
      changeType: averageTenure != null && averageTenure >= 3 ? 'increase' : 'neutral',
      icon: Clock,
      color: 'indigo',
      link: '/admin/employees',
      tooltip: 'Weighted average tenure across all employees'
    },
    {
      title: 'Active Projects',
      value: stats.activeProjects || 0,
      change: 'With staff present',
      changeType: 'neutral',
      tooltip: stats.activeProjects === 0 ? 'No clock-ins recorded yet today' : null,
      icon: MapPin,
      color: 'teal',
      link: '/admin/site'
    },
    {
      title: 'Document Verification',
      value: stats.pendingDocuments || 0,
      change: `${stats.documentsVerifiedToday || 0} verified today`,
      changeType: 'neutral',
      icon: FileText,
      color: 'red',
      link: DOCUMENT_VERIFICATION_QUEUE_HREF,
      showChevron: true,
      tooltip: getDocumentVerificationTooltip(
        stats.pendingDocuments,
        stats.documentsVerifiedToday,
      ),
    },
  ];

  const statCardsWithDRC = statCards;

  if (loading) {
    return (
      <div className="space-y-6 md:space-y-8 bg-gray-50 min-h-screen">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-9 bg-gray-200 rounded-lg w-64 animate-pulse"></div>
            <div className="h-6 bg-gray-200 rounded-lg w-96 animate-pulse"></div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="h-10 bg-gray-200 rounded-full w-32 animate-pulse"></div>
            <div className="h-10 bg-gray-200 rounded-full w-32 animate-pulse"></div>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
              <div className="flex items-center animate-pulse">
                <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gray-200 rounded-full flex-shrink-0"></div>
                <div className="ml-2 sm:ml-4 flex-1">
                  <div className="h-8 bg-gray-200 rounded w-16 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                </div>
              </div>
              <div className="mt-3">
                <div className="h-6 bg-gray-200 rounded-full w-12 animate-pulse"></div>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="loading-spinner-admin mr-3"></div>
          <span className="text-sm text-gray-600">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8 bg-gray-50 min-h-screen">
      {/* Header */}
      <header>
        <div className="flex items-center gap-3 mb-1 min-w-0">
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
          </div>
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">Admin Dashboard</h1>
        </div>
        <p className="text-xs sm:text-sm text-gray-500 ml-11 sm:ml-12">Monitor and manage your HR operations with precision</p>
      </header>

      {/* Executive Summary — RAG Health Strip */}
      <ExecutiveSummaryStrip
        turnoverData={turnoverData}
        overtimeStats={overtimeStats}
        restDayStats={restDayStats}
        drcRatio={drcRatio}
        drcRatioDisplay={drcRatioDisplay}
        complianceExpiryData={complianceExpiryData}
      />

      {/* Analytics Status Indicator */}
      {analyticsStatus.loaded && analyticsStatus.errors.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 mb-6">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-yellow-800">
                Some analytics data is temporarily unavailable ({analyticsStatus.successCount}/{analyticsStatus.totalCount} loaded)
              </p>
              <p className="text-xs text-yellow-600 mt-1">
                {analyticsStatus.errors.includes('gender_distribution_stats') && '• Gender distribution chart unavailable '}
                {analyticsStatus.errors.includes('department_analytics_stats') && '• Department analytics unavailable '}
                {analyticsStatus.errors.length > 2 && `• ${analyticsStatus.errors.length - 2} other analytics unavailable `}
                The main dashboard metrics are working normally.
              </p>
            </div>
            <div className="hidden sm:block text-xs text-yellow-600 flex-shrink-0">
              <p>Charts will show empty states</p>
              <p>until data is available</p>
            </div>
          </div>
        </div>
      )}

      {/* KPI Stats Grid */}
      <section aria-label="Key metrics overview">
        <h2 className="sr-only">Dashboard Statistics</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        {statCardsWithDRC.map((stat, index) => {
          const CardWrapper = stat.onClick ? 'button' : Link;
          const cardProps = stat.onClick
            ? { onClick: stat.onClick }
            : { to: stat.link };
          
          return (
            <CardWrapper
              key={index}
              {...cardProps}
              className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 hover:shadow-md transition-all duration-200 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 relative w-full text-left flex flex-col"
              title={stat.tooltip || undefined}
              aria-label={`${stat.title}: ${stat.value}. Change: ${stat.change}. Click to view details.`}
            >
              {stat.showFreshnessIndicator && (
                <div className="absolute top-2 right-2 z-10">
                  {stat.dataFreshness === 'real-time' && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></div>
                      Live
                    </span>
                  )}
                  {stat.dataFreshness === 'error' && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-300">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Offline
                    </span>
                  )}
                  {stat.dataFreshness === 'fallback' && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Offline
                    </span>
                  )}
                </div>
              )}
              
              {stat.showChevron && (
                <ChevronRight className="absolute top-4 sm:top-6 right-4 sm:right-6 w-4 h-4 sm:w-5 sm:h-5 text-gray-400 group-hover:text-gray-600 transition-colors" aria-hidden="true" />
              )}
              
              <div className="flex items-center">
                <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                  stat.color === 'blue' ? 'bg-blue-100' :
                  stat.color === 'green' ? 'bg-green-100' :
                  stat.color === 'purple' ? 'bg-purple-100' :
                  stat.color === 'yellow' ? 'bg-yellow-100' :
                  stat.color === 'indigo' ? 'bg-indigo-100' :
                  stat.color === 'emerald' ? 'bg-emerald-100' :
                  stat.color === 'red' ? 'bg-red-100' :
                  stat.color === 'teal' ? 'bg-teal-100' :
                  'bg-gray-100'
                }`}>
                  <stat.icon className={`w-4 h-4 sm:w-6 sm:h-6 ${
                    stat.color === 'blue' ? 'text-blue-800' :
                  stat.color === 'green' ? 'text-green-600' :
                  stat.color === 'purple' ? 'text-purple-600' :
                    stat.color === 'yellow' ? 'text-yellow-600' :
                    stat.color === 'indigo' ? 'text-indigo-600' :
                    stat.color === 'emerald' ? 'text-emerald-600' :
                    stat.color === 'red' ? 'text-red-600' :
                    stat.color === 'teal' ? 'text-teal-600' :
                    'text-gray-600'
                  }`} aria-hidden="true" />
                </div>
                <div className="ml-2 sm:ml-4 min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{stat.value}</p>
                  <div className="text-xs sm:text-sm text-gray-600 group-hover:text-gray-700 transition-colors truncate">
                    {stat.title}
                  </div>
                </div>
              </div>
              <div className="mt-auto pt-3 flex items-center">
                <div className={`flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                stat.changeType === 'increase' ? 'bg-green-100 text-green-700' : 
                stat.changeType === 'decrease' ? 'bg-red-100 text-red-700' : 
                stat.changeType === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                  {stat.showArrow !== false && stat.changeType === 'increase' && <ArrowUpRight className="w-3 h-3 mr-1" aria-hidden="true" />}
                  {stat.showArrow !== false && stat.changeType === 'decrease' && <ArrowDownRight className="w-3 h-3 mr-1" aria-hidden="true" />}
                {stat.change}
              </div>
            </div>
              {stat.preview && stat.preview}
            </CardWrapper>
          );
        })}
      </div>
      </section>

      {/* Interim Birth Notification — Overdue Alert */}
      {!interimOverdueLoading && (interimOverdueData?.total_overdue ?? 0) > 0 && (
        <div
          role="alert"
          aria-label="Interim birth notification overdue alert"
          className="flex items-center justify-between gap-3 bg-red-50 border border-red-200 rounded-2xl px-3 sm:px-5 py-3 sm:py-4"
        >
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <div className="w-8 h-8 sm:w-9 sm:h-9 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Baby className="w-4 h-4 text-red-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-semibold text-red-800 truncate">
                {interimOverdueData.total_overdue} interim birth notification{interimOverdueData.total_overdue > 1 ? 's' : ''} overdue
              </p>
              <p className="text-xs text-red-600 mt-0.5 line-clamp-2 sm:line-clamp-1">
                Birth certificate not submitted within 42-day deadline · Paternity Leave &amp; SPL provisional credits at risk
              </p>
            </div>
          </div>
          <Link
            to="/admin/leave"
            className="inline-flex items-center gap-1 text-xs font-medium text-red-700 hover:text-red-900 whitespace-nowrap flex-shrink-0 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 rounded"
            aria-label="View in Leave Management"
          >
            <span className="hidden sm:inline">View in Leave Management</span>
            <span className="sm:hidden">View</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}

      {/* ─── SECTION GROUP: Workforce Health (open by default) ─── */}
      <AccordionSection
        expanded={openSections.workforceHealth}
        onToggle={() => toggleSection('workforceHealth')}
        theme="admin"
        contentVariant="padded"
        icon={<HeartPulse className="w-4 h-4 text-red-600" />}
        iconBg="bg-red-100"
        title="Workforce Health"
      >
        <div className="space-y-6">
          <TurnoverSection
            turnoverData={turnoverData}
            turnoverLoading={turnoverLoading}
            turnoverViewMode={turnoverViewMode}
            setTurnoverViewMode={setTurnoverViewMode}
            showTurnoverInfoModal={showTurnoverInfoModal}
            setShowTurnoverInfoModal={setShowTurnoverInfoModal}
            showCalculationDetails={showCalculationDetails}
            setShowCalculationDetails={setShowCalculationDetails}
          />
          <WorkforcePlanningSection
            retirementRisk={retirementRisk}
            newHireRetention={newHireRetention}
            staffWorkerRatio={staffWorkerRatio}
          />
        </div>
      </AccordionSection>

      {/* ─── SECTION GROUP: Compliance & Regulatory (open by default) ─── */}
      <AccordionSection
        expanded={openSections.compliance}
        onToggle={() => toggleSection('compliance')}
        theme="admin"
        contentVariant="padded"
        icon={<Shield className="w-4 h-4 text-red-600" />}
        iconBg="bg-red-100"
        title="Compliance & Regulatory"
      >
        <div className="space-y-6">
          <ComplianceExpirySection
            complianceExpiryData={complianceExpiryData}
            complianceExpiryLoading={complianceExpiryLoading}
          />
          <LevyOptimizationSection />
          <DRCSection
            drcData={drcData}
            drcLoading={drcLoading}
            drcViewMode={drcViewMode}
            setDrcViewMode={setDrcViewMode}
            drcRatio={drcRatio}
            drcRatioDisplay={drcRatioDisplay}
            lqsCount={lqsCount}
            migrantCount={migrantCount}
            foreignTotal={foreignTotal}
            foreignShare={foreignShare}
            foreignShareDisplay={foreignShareDisplay}
            wpCount={wpCount}
            spCount={spCount}
            epCount={epCount}
            localEmployeesCount={localEmployeesCount}
            momLimit={momLimit}
            maxForeignWorkers={maxForeignWorkers}
            bufferCapacity={bufferCapacity}
            ttmChartData={ttmChartData}
            yoyChartData={yoyChartData}
            ttmYAxisDomain={ttmYAxisDomain}
            yoyYAxisDomain={yoyYAxisDomain}
            trendRange={trendRange}
            minDrc={minDrc}
            maxDrc={maxDrc}
            yoyYears={yoyYears}
          />
          <MOMComplianceSection
            overtimeStats={overtimeStats}
            overtimeAlerts={overtimeAlerts}
            overtimeLoading={overtimeLoading}
            restDayStats={restDayStats}
            restDayLoading={restDayLoading}
          />
          <SiteWeatherSection />
        </div>
      </AccordionSection>

      {/* ─── SECTION GROUP: Cost & Finance (collapsed by default) ─── */}
      <AccordionSection
        expanded={openSections.costFinance}
        onToggle={() => toggleSection('costFinance')}
        theme="admin"
        contentVariant="padded"
        icon={<Wallet className="w-4 h-4 text-red-600" />}
        iconBg="bg-red-100"
        title="Cost & Finance"
      >
        <ProjectLabourCostSection />
      </AccordionSection>

      {/* ─── SECTION GROUP: Workforce Profile (collapsed by default) ─── */}
      <AccordionSection
        expanded={openSections.workforceProfile}
        onToggle={() => toggleSection('workforceProfile')}
        theme="admin"
        contentVariant="padded"
        icon={<PieChart className="w-4 h-4 text-red-600" />}
        iconBg="bg-red-100"
        title="Workforce Profile"
      >
        <WorkforceAnalyticsSection
          ageGroupData={ageGroupData}
          workforceAgeProfile={workforceAgeProfile}
          nationalityData={nationalityData}
          raceData={raceData}
          genderData={genderData}
          tenureData={tenureData}
          departmentData={departmentData}
          showDepartmentModal={showDepartmentModal}
          setShowDepartmentModal={setShowDepartmentModal}
          stats={stats}
        />
      </AccordionSection>

      {/* ─── SECTION GROUP: Platform Usage (collapsed by default) ─── */}
      <AccordionSection
        expanded={openSections.platformUsage}
        onToggle={() => toggleSection('platformUsage')}
        theme="admin"
        contentVariant="padded"
        icon={<Activity className="w-4 h-4 text-red-600" />}
        iconBg="bg-red-100"
        title="Platform Usage & Adoption"
      >
        <UsageAnalyticsSummary />
      </AccordionSection>

      {/* Workforce Composition Modal */}
      <WorkforceCompositionModal
        isOpen={showWorkforceModal}
        onClose={() => setShowWorkforceModal(false)}
        data={employmentPassData}
      />

      <HeadcountChangeModal
        isOpen={showHeadcountModal}
        onClose={() => setShowHeadcountModal(false)}
        data={headcountMovements}
        loading={headcountMovementsLoading}
        error={headcountMovementsError}
      />
    </div>
  );
};

export default AdminDashboard;
